import React from 'react';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { ArrowRight } from 'lucide-react';

export default function TagHistoryDialog({ 
  open, 
  onClose, 
  tag, 
  reportIssues, 
  progressReports, 
  officers,
  activities 
}) {
  // Find all instances of this tag
  const tagFieldMap = {
    'tagged_officers': 'tagged_officers',
    'tagged_artifacts': 'tagged_artifacts',
    'tagged_rituals': 'tagged_rituals',
    'tagged_policies': 'tagged_policies',
    'tagged_capabilities': 'tagged_capabilities'
  };

  const tagField = tag ? tagFieldMap[tag.tagType] : null;
  
  // Group instances by activity
  const instancesByActivity = React.useMemo(() => {
    if (!tag) return {};
    const grouped = {};
    
    reportIssues
      .filter(issue => {
        const tags = issue[tagField] || [];
        return tags.includes(tag.tagValue);
      })
      .forEach(issue => {
        const report = progressReports.find(r => r.id === issue.progress_report_id);
        const activity = activities?.find(a => a.id === report?.activity_id);
        
        if (!activity) return;
        
        const activityId = activity.id;
        if (!grouped[activityId]) {
          grouped[activityId] = {
            activity,
            instances: []
          };
        }
        
        grouped[activityId].instances.push({
          issue,
          report,
          officer: officers.find(o => o.user_email === report?.reported_by),
          issueDescription: issue.issue_description
        });
      });
    
    // Sort instances within each activity by date
    Object.values(grouped).forEach(group => {
      group.instances.sort((a, b) => new Date(b.report?.reported_at) - new Date(a.report?.reported_at));
    });
    
    return grouped;
  }, [reportIssues, progressReports, activities, officers, tagField, tag]);

  if (!tag) return null;

  const getTagLabel = () => {
    if (tag.tagType === 'tagged_officers') {
      const officer = officers.find(o => o.user_email === tag.tagValue);
      return officer?.callsign || tag.tagValue;
    }
    return tag.tagValue;
  };

  const getTagTypeLabel = () => {
    return tag.tagType.replace('tagged_', '').replace('_', ' ');
  };

  const groupedActivities = Object.values(instancesByActivity);

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="act-taghistorydialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Tag History: {getTagLabel()} ({getTagTypeLabel()})
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {groupedActivities.length > 0 ? (
            groupedActivities.map(({ activity, instances }) => (
              <div key={activity.id} className="space-y-2">
                {/* Activity Header - Clickable */}
                <Link to={createPageUrl('activities')}>
                  <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:bg-slate-800 hover:border-indigo-500 transition-all cursor-pointer flex items-center justify-between group">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-200 group-hover:text-indigo-400 transition-colors">
                        [{activity.activity_code}] {activity.title}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {activity.responsible_email?.split('@')[0]}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-indigo-400 transition-colors" />
                  </div>
                </Link>
                
                {/* Instances for this activity */}
                <div className="space-y-2 ml-2">
                  {instances.map((instance, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 bg-slate-950 rounded-lg border border-slate-800 space-y-2 text-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-300">
                          {instance.officer?.callsign || instance.report?.reported_by || 'Unknown'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {instance.report?.reported_at 
                            ? format(new Date(instance.report.reported_at), 'MMM d, HH:mm')
                            : 'Unknown date'
                          }
                        </span>
                      </div>
                      
                      <div className="text-slate-400">
                        <span className="text-slate-500">Issue: </span>
                        {instance.issueDescription || 'No description'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-slate-500">
              No instances found for this tag
            </div>
          )}
        </div>
      </DialogContent>
    </ConfirmableDialog>
  );
}